import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDeepMemo } from '../../../hooks/useCompare';
import { isArray, isObject } from '../../../utils/guards';
import { scrollToFilterByKey } from '../DataTableAdvancedElements/DataTableFilters/scrollToFilterByKey';
import { usePluginContext } from '../plugins/impl';
import { QueryModel } from './DataTableQueryProvider/DataTableQuery.types';
import { useDataTableInitialSearchModel } from './DataTableQueryProvider/DataTableInitialSearchModelProvider';
import { useDataTableQueryContext } from './DataTableQueryProvider/DataTableQueryProvider';

export interface FilterDefinition {
  id: string;
  label: string;
}

export interface FilterDefinitionWithValue extends FilterDefinition {
  value: unknown;
}

export interface DataTableFiltersContextProps {
  setVisibleFilters: (filters: 'all' | string[], notify?: boolean) => void;
  clearVisibleFilters: (filters: 'all' | string[], notify?: boolean) => void;
  visibleFilters: 'all' | string[];

  filterDefinitions: FilterDefinition[];
  setFilterDefinitions: (definitions: FilterDefinition[]) => void;

  isFilterDrawerOpen: boolean;
  setOpenFilterDrawer: (show: boolean, scrollTo?: string) => void;

  hasFilters: boolean;

  hiddenVariables: (keyof QueryModel)[];
  activeFilters: FilterDefinitionWithValue[];

  clearFilter: (id: string) => void;
  clearFilterAll: () => void;
}

export const DataTableFiltersContext = createContext<DataTableFiltersContextProps>({
  setVisibleFilters: () => {},
  clearVisibleFilters: () => {},
  visibleFilters: [],
  filterDefinitions: [],
  setFilterDefinitions: () => {},
  isFilterDrawerOpen: false,
  setOpenFilterDrawer: () => {},
  hasFilters: false,
  hiddenVariables: [],
  activeFilters: [],
  clearFilter: () => {},
  clearFilterAll: () => {}
});

interface DataTableFiltersProviderProps {
  hiddenVariables: (keyof QueryModel)[];
}

export const DataTableFiltersProvider = ({
  children,
  hiddenVariables
}: PropsWithChildren<DataTableFiltersProviderProps>) => {
  const { initialSearchModel } = useDataTableInitialSearchModel();
  const { queryVariables, applyQuery } = useDataTableQueryContext();
  const hasFilters = usePluginContext().isDestinationDefined('filters');

  const [filterDefinitions, setFilterDefinitions] = useState<{ id: string; label: string }[]>([]);
  const [visibleFilters, setVisibleFiltersInternal] = useState<string[] | 'all'>([]);
  const [isFilterDrawerOpen, setInternalOpenFilterDrawer] = useState(false);

  const setVisibleFilters = useCallback((ids: string[] | 'all') => {
    setVisibleFiltersInternal((prevState) => {
      if (prevState === 'all') {
        return prevState;
      }
      return ids;
    });
  }, []);

  const clearVisibleFilters = useCallback((ids: string[] | 'all') => {
    setVisibleFiltersInternal((prevState) => {
      if (prevState === 'all') {
        return prevState;
      }
      return prevState.filter((filter) => !ids.includes(filter));
    });
  }, []);

  const activeFilters = useMemo(() => {
    const keys = Array.from(new Set(Object.keys(queryVariables?.query ?? {})));
    return keys.reduce<{ id: string; label: string; value: unknown }[]>((acc, id) => {
      const value = queryVariables?.query?.[id];
      const label = filterDefinitions.find((def) => def.id === id)?.label ?? id;
      const isSearchFilter = id === 'search';
      const isDuplicateLabel = !!acc.find((it) => it.label === label);
      if (
        isSearchFilter ||
        isDuplicateLabel ||
        isFalsyValue(value) ||
        (isArray(value) && value.length === 0) ||
        (isObject(value) && Object.entries(value).filter(([, val]) => !isFalsyValue(val)).length === 0)
      ) {
        return acc;
      }
      return [
        ...acc,
        {
          id,
          label: filterDefinitions.find((def) => def.id === id)?.label ?? id,
          value
        }
      ];
    }, []);
  }, [filterDefinitions, queryVariables?.query]);

  const clearFilter = useCallback(
    (id: string) => {
      applyQuery({ [id]: undefined }, { persist: true, mode: 'patch' });
      clearVisibleFilters([id]);
    },
    [applyQuery, clearVisibleFilters]
  );

  const clearFilterAll = useCallback(
    (keepSearch = true) => {
      applyQuery(
        { search: keepSearch ? (queryVariables?.query?.search ?? undefined) : undefined },
        { persist: true, mode: 'replace' }
      );
      clearVisibleFilters('all');
    },
    [applyQuery, clearVisibleFilters, queryVariables?.query?.search]
  );

  useEffect(() => {
    if (!!initialSearchModel) {
      setVisibleFilters(Object.keys(initialSearchModel.query ?? {}));
    }
  }, [initialSearchModel, setVisibleFilters]);

  const setOpenFilterDrawer = useCallback((open: boolean, scrollTo?: string) => {
    setInternalOpenFilterDrawer(open);
    if (scrollTo) {
      scrollToFilterByKey(scrollTo);
    }
  }, []);

  const contextValue = useDeepMemo(
    () => ({
      hasFilters,
      setVisibleFilters,
      clearVisibleFilters,
      visibleFilters,
      hiddenVariables,
      filterDefinitions,
      setFilterDefinitions,
      isFilterDrawerOpen,
      setOpenFilterDrawer,
      activeFilters,
      clearFilter,
      clearFilterAll
    }),
    [
      hasFilters,
      setVisibleFilters,
      clearVisibleFilters,
      visibleFilters,
      hiddenVariables,
      filterDefinitions,
      isFilterDrawerOpen,
      setOpenFilterDrawer,
      activeFilters,
      clearFilter,
      clearFilterAll
    ]
  );

  return <DataTableFiltersContext.Provider value={contextValue}>{children}</DataTableFiltersContext.Provider>;
};

export const useDataTableFilterContext = (): DataTableFiltersContextProps => {
  const context = useContext<DataTableFiltersContextProps>(DataTableFiltersContext);

  if (!context) {
    throw new Error('useDataTableFilterContext must be used under DataTableFiltersContext');
  }
  return context;
};

function isFalsyValue(value: unknown) {
  return typeof value === 'undefined' || value === null || value === '' || (isArray(value) && value.length === 0);
}
